import React, { SyntheticEvent, useMemo, useEffect } from 'react';
import { useLazyQuery, useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';
import _uniqBy from 'lodash/uniqBy';

import { IconUserFolder, LoadingSpinner, Tooltip } from '@trqk/components';
import { locale, getWebpackEnvVar, sortAlphanumeric } from '@trqk/utils';
import { queryStrings } from '@trqk/resolvers';
import { useWheelEvent } from '@trqk/hooks';

import { useTokenModal } from '../../../../hooks';
import { EntitySelectorTooltipContent } from '.';
import { EntityManagerTable } from './EntityManagerTable';
import { entityManagerTableDimensions } from '../constants';
import { FULL_PATHS, HASHES } from '../../../../constants';

const { GET_ENTITIES_WITH_UPLOADED_STATEMENTS, GET_SESSION_CURRENT_USER } =
    queryStrings;

/**
 * Displays some helpful information about how entity selection works, as well as a button to
 * upgrade the user's subscription so they can use more entities. Features a table of the the user's
 * current entities, which are fetched with getEntitiesWithUploadedStatements.
 * @param props {EntityManagerProps}
 */
export const EntityManager = (): JSX.Element => {
    const trqkDirectoryUrl = getWebpackEnvVar('web', 'TRQK_DIRECTORY_URL');
    const currentUserQuery = useQuery(GET_SESSION_CURRENT_USER);
    const userId = currentUserQuery?.data?.session?.currentUser?.id;
    const { getToken } = useTokenModal();
    const [
        getEntitiesWithUploadedStatements,
        getEntitiesWithUploadedStatementsQuery,
    ] = useLazyQuery(GET_ENTITIES_WITH_UPLOADED_STATEMENTS, {
        // This prevents a loading flash in the EntityManagerTable when clicking on a delete button
        // to open the delete confirmation modal
        fetchPolicy: 'cache-and-network',
    });

    useEffect(() => {
        if (userId && trqkDirectoryUrl) {
            getEntitiesWithUploadedStatements({
                variables: {
                    trqkDirectoryUrl,
                },
                context: {
                    getToken,
                },
            });
        }
    }, [getEntitiesWithUploadedStatements, getToken, trqkDirectoryUrl, userId]);

    const entities =
        getEntitiesWithUploadedStatementsQuery?.data
            ?.getEntitiesWithUploadedStatements
            ?.entitiesWithUploadedStatements ??
        ([] as EntityWithUploadedStatements[]);

    const sortedEntities = useMemo((): EntityWithUploadedStatements[] => {
        const dedupedEntities = _uniqBy<EntityWithUploadedStatements>(
            entities,
            'id',
        );

        return dedupedEntities.sort(
            (entityA: TrqkEntity, entityB: TrqkEntity): number =>
                sortAlphanumeric(entityA.name, entityB.name),
        );
    }, [entities]);

    const stopPropagationForWheel = useWheelEvent(500, (e: Event) => {
        e.stopPropagation();
    });

    return (
        <div
            className="px-12 py-10 bg-white rounded-lg"
            onScroll={(e: SyntheticEvent): void => {
                // Don't scroll in the parent when this section is being scrolled
                e.stopPropagation();
            }}
            onWheel={(): void => {
                // Don't wheel in the parent when this section is being wheeled
                stopPropagationForWheel();
            }}
        >
            <div className="flex items-center justify-between">
                <IconUserFolder
                    fillColor="primary-500"
                    width={35}
                    addClasses="flex-none"
                />
                <h2
                    className="text-xl ml-2 flex-grow"
                    data-cy="entity-manager-section-title"
                >
                    {locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.TITLE}
                </h2>
            </div>
            <div className="my-8 leading-8">
                <div>
                    {locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.CTA.PART_1}
                    &nbsp;
                    <Link
                        className="underline text-primary-500"
                        to={({ search }) => ({
                            hash: HASHES.MANAGE_STATEMENTS.UPLOADS,
                            pathname: FULL_PATHS.MANAGE_STATEMENTS_CHARTS,
                            search,
                        })}
                    >
                        {
                            locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.CTA
                                .UPLOAD_A_STATEMENT
                        }
                    </Link>
                    &nbsp;
                    {locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.CTA.PART_2}
                </div>
                <div>
                    {locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.CTA.PART_3}
                    &nbsp;
                    <Tooltip
                        messageClasses="border-l-4 border-primary-500"
                        background="light"
                        maxWidth={400}
                        minWidth={400}
                        RenderMessage={EntitySelectorTooltipContent}
                        position="bottom"
                    >
                        <span className="text-primary-500 border-dashed border-b-2 border-primary-500">
                            {
                                locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.CTA
                                    .ENTITY_SELECTOR
                            }
                        </span>
                    </Tooltip>
                    &nbsp;
                    {locale.enUS.ACCOUNT.SECTIONS.ENTITY_MANAGER.CTA.PART_4}
                </div>
            </div>
            {!getEntitiesWithUploadedStatementsQuery.data && (
                <div
                    className="flex justify-center items-center"
                    style={{ height: entityManagerTableDimensions.tableHeight }}
                >
                    <LoadingSpinner />
                </div>
            )}
            {getEntitiesWithUploadedStatementsQuery.data && (
                <EntityManagerTable entities={sortedEntities} />
            )}
        </div>
    );
};
